/**
 * ProjectOverviewPage
 *
 * Project hub at /all-projects/:projectId.
 * Hash-based sections:
 *   #overview (default) — project summary stats + app launchers
 *   #meters — location form + meters & tariffs
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    Button,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    ArrowBack,
    SolarPower,
    ElectricCar,
    AcUnit,
    Monitor,
    Memory,
    Launch as LaunchIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    DeleteOutline,
    LocationOn,
    ElectricBolt,
    Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import {
    vortxColors,
    useUnifiedProjects,
    useRetroContextSafe,
    useAuth,
    useThemeMode,
    API_BASE_URL,
    MetersAndTariffsSection,
    SaveStatusIndicator,
    vortxTypography,
} from '@vortx/shared-ui';
import { decomposeMeterLoad } from '@vortx/flux-cold-storage-web';
import LoadProfileChart from '../LoadProfileChart';

const LoadProfileTerrain = React.lazy(() =>
    import('../../../vortx-shared-ui/src/data-display/LoadProfileTerrain')
);

class TerrainErrorBoundary extends React.Component {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    render() {
        if (this.state.hasError) return this.props.fallback;
        return this.props.children;
    }
}

const APP_CONFIG = {
    site_dna: { label: 'Site DNA', icon: SolarPower, color: '#ffa500', route: (id) => `/projects/${id}/analysis`, model: 'Solar + Storage Optimization' },
    flux_ev: { label: 'Flux EV', icon: ElectricCar, color: '#4CAF50', route: (id) => `/ev/${id}`, model: 'Fleet Charging Optimization' },
    flux_cold: { label: 'Flux COLD', icon: AcUnit, color: '#2196F3', route: (id) => `/cold-storage/${id}`, model: 'Thermal Load Optimization' },
    flux_ems: { label: 'Flux EMS', icon: Monitor, color: '#5fd4c4', route: (id) => `/ems/${id}`, model: 'Energy Management System' },
    flux_dc: { label: 'Flux DC', icon: Memory, color: '#ff9800', route: (id) => `/dc/${id}`, model: 'Data Center Energy Management' },
};

const APP_ORDER = ['site_dna', 'flux_ev', 'flux_cold', 'flux_ems', 'flux_dc'];

const US_STATES = [
    { value: '', label: 'Select State' },
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' },
];

const getFieldSx = (isRetro) => ({
    '& .MuiOutlinedInput-root': {
        color: isRetro ? vortxColors.white : 'text.primary',
        '& fieldset': { borderColor: isRetro ? vortxColors.teal : 'rgba(0,0,0,0.23)' },
        '&:hover fieldset': { borderColor: vortxColors.gold },
        '&.Mui-focused fieldset': { borderColor: vortxColors.gold, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': {
        color: isRetro ? vortxColors.stone : 'text.secondary',
        '&.Mui-focused': { color: vortxColors.gold },
    },
});

export default function ProjectOverviewPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { currentUser } = useAuth();
    const { activeProject, setActiveProject, clearProjectScope } = useRetroContextSafe();
    const { isRetro } = useThemeMode();
    const { projects, loading, updateProject, enableApp, disableApp } = useUnifiedProjects(null);
    const fieldSx = getFieldSx(isRetro);

    // Determine active section from hash
    const activeSection = routerLocation.hash === '#meters' ? 'meters' : 'overview';

    // Find project from list or use activeProject
    const project = projects.find(p => p.id === projectId) || activeProject;

    // Local form state
    const [editingName, setEditingName] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState({ address: '', city: '', state: '', zip_code: '' });
    const [siteAmps, setSiteAmps] = useState('');
    const [serviceVoltage, setServiceVoltage] = useState('');
    const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [removeDialog, setRemoveDialog] = useState({ open: false, appKey: null });
    const [scenarioCount, setScenarioCount] = useState(null);
    const [terrainDialogOpen, setTerrainDialogOpen] = useState(false);

    // Only sync form fields from project on initial load (not after every save)
    const initializedForId = useRef(null);
    const geocodedForId = useRef(null);

    useEffect(() => {
        if (project) {
            if (initializedForId.current !== project.id) {
                initializedForId.current = project.id;
                setName(project.name || '');
                setDescription(project.description || '');
                setLocation({
                    address: project.location?.address || '',
                    city: project.location?.city || '',
                    state: project.location?.state || '',
                    zip_code: project.location?.zip_code || project.location?.zip || '',
                });
                setSiteAmps(project.site_capacity_amps != null ? String(project.site_capacity_amps) : '');
                setServiceVoltage(project.service_voltage != null ? String(project.service_voltage) : '');
            }
            if (!activeProject || activeProject.id !== project.id) {
                setActiveProject(project);
            }
        }
    }, [project, activeProject, setActiveProject]);

    // Fetch Site DNA scenario count for project overview card
    useEffect(() => {
        if (!projectId || !currentUser || !project?.apps?.site_dna) {
            setScenarioCount(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(
                    `${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios`,
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (!res.ok || cancelled) return;
                const data = await res.json();
                if (!cancelled) setScenarioCount(data.count ?? data.scenarios?.length ?? 0);
            } catch (err) {
                console.error('Failed to fetch scenario count:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [projectId, currentUser, project?.apps?.site_dna]);

    const saveField = useCallback(async (updates) => {
        if (!projectId) return;
        setSaveStatus('saving');
        try {
            const updated = await updateProject(projectId, updates);
            setActiveProject(updated);
            setSaveStatus('saved');
            setLastSavedAt(new Date().toISOString());
            setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (err) {
            console.error('Failed to save:', err);
            setSaveStatus('idle');
        }
    }, [projectId, updateProject, setActiveProject]);

    // Auto-geocode on load if project has a zip but no valid lat/lon
    useEffect(() => {
        if (!project || !currentUser || geocodedForId.current === project.id) return;
        const loc = project.location || {};
        const zip = (loc.zip_code || loc.zip || '').trim();
        const hasValidCoords = loc.lat && loc.lon && !(loc.lat === 0 && loc.lon === 0);
        const hasGeocodableInfo = (zip && /^\d{5}$/.test(zip)) || (loc.city && loc.state);
        if (hasGeocodableInfo && !hasValidCoords) {
            geocodedForId.current = project.id;
            (async () => {
                try {
                    const parts = [loc.address, loc.city, loc.state, zip].filter(Boolean);
                    const geocodeAddress = parts.join(', ') || zip;

                    const token = await currentUser.getIdToken();
                    const res = await fetch(`${API_BASE_URL}/api/projects/v1/geocode`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ address: geocodeAddress }),
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data.lat && data.lon) {
                            saveField({ location: { ...loc, lat: data.lat, lon: data.lon } });
                        }
                    }
                } catch (err) {
                    console.warn('Auto-geocoding failed:', err);
                }
            })();
        }
    }, [project, currentUser, saveField]);

    const handleNameSave = async () => {
        if (!name.trim() || name === project?.name) {
            setEditingName(false);
            return;
        }
        await saveField({ name: name.trim() });
        setEditingName(false);
    };

    const handleDescriptionBlur = () => {
        if (description !== (project?.description || '')) {
            saveField({ description });
        }
    };

    const handleLocationSave = useCallback(async (locData) => {
        const locToSave = locData || location;
        const current = project?.location || {};

        const hasChanged =
            locToSave.address !== (current.address || '') ||
            locToSave.city !== (current.city || '') ||
            locToSave.state !== (current.state || '') ||
            locToSave.zip_code !== (current.zip_code || current.zip || '');

        if (!hasChanged) return;

        let geocoded = { lat: current.lat, lon: current.lon };
        const zip = locToSave.zip_code?.trim();
        const zipChanged = zip !== (current.zip_code || current.zip || '').trim();

        if (zip && /^\d{5}$/.test(zip) && zipChanged && currentUser) {
            try {
                const parts = [locToSave.address, locToSave.city, locToSave.state, zip].filter(Boolean);
                const geocodeAddress = parts.join(', ') || zip;

                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/projects/v1/geocode`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ address: geocodeAddress }),
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.lat && data.lon) {
                        geocoded = { lat: data.lat, lon: data.lon };
                    }
                }
            } catch (err) {
                console.warn('Geocoding failed:', err);
            }
        }

        saveField({ location: { ...locToSave, ...geocoded } });
    }, [location, project, currentUser, saveField]);

    const handleLocationBlur = () => {
        handleLocationSave();
    };

    const handleUpdateMeters = async (meters) => {
        const updates = { meters };

        if (meters.ev_submeter && project?.apps?.flux_ev?.enabled) {
            updates.apps = {
                ...project.apps,
                flux_ev: {
                    ...project.apps.flux_ev,
                    meter: 'ev_submeter',
                },
            };
        }

        if (!meters.ev_submeter && project?.apps?.flux_ev?.meter === 'ev_submeter') {
            updates.apps = {
                ...project.apps,
                flux_ev: {
                    ...project.apps.flux_ev,
                    meter: 'main',
                },
            };
        }

        await saveField(updates);
    };

    const handleUpdateAppConfig = async (appKey, updates) => {
        const currentAppConfig = project?.apps?.[appKey] || {};
        await saveField({
            apps: {
                ...project.apps,
                [appKey]: {
                    ...currentAppConfig,
                    ...updates,
                },
            },
        });
    };

    /**
     * Decompose the facility meter load into cold storage and residual components.
     * Calls the Flux COLD decompose API via the service function, then persists
     * results onto meter.load_profile.decompositions.flux_cold.
     */
    const handleDecompose = useCallback(async () => {
        if (!project || !currentUser) return;

        const mainMeter = project.meters?.main;
        const loadProfile = mainMeter?.load_profile;
        if (!loadProfile?.loads_kw || loadProfile.loads_kw.length !== 8760) return;

        const coldConfig = project.apps?.flux_cold?.config || {};
        const zones = coldConfig.zones || [];
        if (zones.length === 0) return;

        const location = project.location || {};
        const loadProfileYear = loadProfile.year || new Date().getFullYear();

        const result = await decomposeMeterLoad(
            loadProfile.loads_kw,
            { lat: location.lat || 0, lng: location.lon || location.lng || 0 },
            loadProfileYear,
            zones,
            currentUser
        );

        // Persist decomposition results on the load profile
        await saveField({
            meters: {
                ...project.meters,
                main: {
                    ...mainMeter,
                    load_profile: {
                        ...loadProfile,
                        decompositions: {
                            ...loadProfile.decompositions,
                            flux_cold: result,
                        },
                    },
                },
            },
        });
    }, [project, currentUser, saveField]);

    const handleAppClick = (appKey) => {
        const config = APP_CONFIG[appKey];
        if (!config) return;
        const route = config.route(projectId);
        navigate(route);
    };

    const handleEnableApp = async (appKey) => {
        try {
            const updated = await enableApp(projectId, appKey, {});
            setActiveProject(updated);
        } catch (err) {
            console.error('Failed to enable app:', err);
        }
    };

    const getAppDataDescription = (appKey) => {
        const appConfig = project?.apps?.[appKey];
        if (!appConfig) return null;

        if (appKey === 'site_dna' && Array.isArray(appConfig)) {
            const count = appConfig.filter(s => s.enabled).length;
            return count > 0 ? `${count} scenario${count !== 1 ? 's' : ''}` : null;
        }
        if (appKey === 'flux_cold') {
            const zones = appConfig.config?.zones;
            if (Array.isArray(zones) && zones.length > 0) {
                return `${zones.length} zone${zones.length !== 1 ? 's' : ''}`;
            }
        }
        if (appKey === 'flux_ev') {
            if (appConfig.depot_id) return '1 depot configuration';
        }
        return 'all configuration data';
    };

    const getAppConfigDetail = (appKey) => {
        const appConfig = project?.apps?.[appKey];
        if (!appConfig) return null;
        switch (appKey) {
            case 'site_dna': {
                if (scenarioCount != null) {
                    if (scenarioCount === 0) return 'No scenarios configured';
                    return `${scenarioCount} scenario${scenarioCount !== 1 ? 's' : ''} configured`;
                }
                return 'Loading...';
            }
            case 'flux_ev': {
                const fleet = appConfig.config?.fleet;
                if (Array.isArray(fleet) && fleet.length > 0) {
                    const vehicleCount = fleet.reduce((sum, v) => sum + (v.quantity || 0), 0);
                    return `${vehicleCount} vehicle${vehicleCount !== 1 ? 's' : ''} configured`;
                }
                return 'No fleet configured';
            }
            case 'flux_cold': {
                const zones = appConfig.config?.zones;
                return (Array.isArray(zones) && zones.length > 0)
                    ? `${zones.length} zone${zones.length !== 1 ? 's' : ''} configured`
                    : 'No zones configured';
            }
            case 'flux_ems': {
                const modes = appConfig.modes;
                return (Array.isArray(modes) && modes.length > 0)
                    ? modes.map(m => m.replace(/_/g, ' ')).join(' · ')
                    : 'No modes configured';
            }
            case 'flux_phd':
                return 'Topology analysis';
            default:
                return null;
        }
    };

    const handleRemoveApp = async () => {
        const { appKey } = removeDialog;
        if (!appKey) return;
        try {
            const updated = await disableApp(projectId, appKey);
            setActiveProject(updated);
        } catch (err) {
            console.error('Failed to remove app:', err);
        }
        setRemoveDialog({ open: false, appKey: null });
    };

    if (loading && !project) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress sx={{ color: vortxColors.gold }} />
            </Box>
        );
    }

    if (!project) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="warning">Project not found</Alert>
            </Box>
        );
    }

    const isAppEnabled = (appKey) => {
        const appConfig = project.apps?.[appKey];
        if (!appConfig) return false;
        if (Array.isArray(appConfig)) return appConfig.some(s => s.enabled);
        return !!appConfig.enabled;
    };

    const enabledApps = APP_ORDER.filter(isAppEnabled);
    const disabledApps = APP_ORDER.filter(k => !isAppEnabled(k));
    const loc = project.location || {};
    const locationSummary = [loc.city, loc.state].filter(Boolean).join(', ') || loc.address || 'Not set';
    const tariffName = project.meters?.main?.tariff?.name;

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Header — shared across both sections */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <IconButton
                    onClick={() => { clearProjectScope(); navigate('/all-projects'); }}
                    sx={{ color: vortxColors.gold }}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    {editingName ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoFocus
                                size="small"
                                onKeyDown={(e) => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') { setName(project.name || ''); setEditingName(false); } }}
                                sx={{
                                    ...fieldSx,
                                    flex: 1,
                                    '& .MuiOutlinedInput-root': {
                                        ...fieldSx['& .MuiOutlinedInput-root'],
                                        fontSize: '1.5rem',
                                        fontFamily: vortxTypography.heading,
                                        fontWeight: 700,
                                    },
                                }}
                            />
                            <IconButton onClick={handleNameSave} sx={{ color: vortxColors.gold }}>
                                <CheckIcon />
                            </IconButton>
                            <IconButton onClick={() => { setName(project.name || ''); setEditingName(false); }} sx={{ color: vortxColors.stone }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontFamily: vortxTypography.heading,
                                    fontWeight: 700,
                                    color: vortxColors.gold,
                                }}
                            >
                                {project.name}
                            </Typography>
                            <IconButton onClick={() => setEditingName(true)} sx={{ color: vortxColors.stone, '&:hover': { color: vortxColors.gold } }}>
                                <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        placeholder="Project description..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onBlur={handleDescriptionBlur}
                        size="small"
                        variant="standard"
                        sx={{
                            mt: 0.5,
                            '& .MuiInput-root': { color: vortxColors.stone, fontSize: '0.875rem' },
                            '& .MuiInput-root:before': { borderBottomColor: 'transparent' },
                            '& .MuiInput-root:hover:before': { borderBottomColor: vortxColors.teal },
                            '& .MuiInput-root:after': { borderBottomColor: vortxColors.gold },
                        }}
                    />
                </Box>
                <SaveStatusIndicator
                    status={saveStatus}
                    lastSavedAt={lastSavedAt}
                />
            </Box>

            {/* ===== OVERVIEW SECTION ===== */}
            {activeSection === 'overview' && (
                <>
                    {/* Project Info Strip */}
                    <Box sx={{
                        display: 'flex',
                        gap: 3,
                        mb: 4,
                        p: 2,
                        bgcolor: isRetro ? vortxColors.charcoal : 'background.paper',
                        border: `1px solid ${isRetro ? vortxColors.teal : 'rgba(0,0,0,0.12)'}`,
                        borderRadius: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
                            <LocationOn sx={{ color: vortxColors.gold, fontSize: 18 }} />
                            <Box>
                                <Typography sx={{
                                    color: vortxColors.stone,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    textTransform: 'uppercase',
                                    fontSize: '0.6rem',
                                    letterSpacing: '0.08em',
                                }}>
                                    Location
                                </Typography>
                                <Typography sx={{ color: isRetro ? vortxColors.white : 'text.primary', fontSize: '0.85rem', lineHeight: 1.2 }}>
                                    {locationSummary}
                                    {loc.zip_code && (
                                        <span style={{ color: vortxColors.stone, marginLeft: 8 }}>{loc.zip_code}</span>
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ width: '1px', height: 32, bgcolor: isRetro ? vortxColors.teal : 'divider', flexShrink: 0 }} />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
                            <ElectricBolt sx={{ color: vortxColors.gold, fontSize: 18 }} />
                            <Box>
                                <Typography sx={{
                                    color: vortxColors.stone,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    textTransform: 'uppercase',
                                    fontSize: '0.6rem',
                                    letterSpacing: '0.08em',
                                }}>
                                    Tariff
                                </Typography>
                                <Typography sx={{ color: isRetro ? vortxColors.white : 'text.primary', fontSize: '0.85rem', lineHeight: 1.2 }}>
                                    {tariffName || 'Not configured'}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ width: '1px', height: 32, bgcolor: isRetro ? vortxColors.teal : 'divider', flexShrink: 0 }} />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                            <ElectricBolt sx={{ color: vortxColors.gold, fontSize: 18 }} />
                            <Box>
                                <Typography sx={{
                                    color: vortxColors.stone,
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    textTransform: 'uppercase',
                                    fontSize: '0.6rem',
                                    letterSpacing: '0.08em',
                                }}>
                                    Service
                                </Typography>
                                <Typography sx={{ color: isRetro ? vortxColors.white : 'text.primary', fontSize: '0.85rem', lineHeight: 1.2 }}>
                                    {project.site_capacity_amps
                                        ? `${project.site_capacity_amps}A · ${project.service_voltage || '—'}V`
                                        : 'Not set'}
                                    {project.site_capacity_kw > 0 && (
                                        <span style={{ color: vortxColors.stone, marginLeft: 8 }}>
                                            ({Math.round(project.site_capacity_kw)} kW)
                                        </span>
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ width: '1px', height: 32, bgcolor: isRetro ? vortxColors.teal : 'divider', flexShrink: 0 }} />

                        <Box>
                            <Typography sx={{
                                color: vortxColors.stone,
                                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                textTransform: 'uppercase',
                                fontSize: '0.6rem',
                                letterSpacing: '0.08em',
                            }}>
                                Coordinates
                            </Typography>
                            <Typography sx={{
                                color: vortxColors.stone,
                                fontSize: '0.8rem',
                                fontFamily: vortxTypography.mono,
                            }}>
                                {loc.lat && loc.lon
                                    ? `${Number(loc.lat).toFixed(4)}, ${Number(loc.lon).toFixed(4)}`
                                    : '\u2014'}
                            </Typography>
                        </Box>

                        <Box sx={{ ml: 'auto' }}>
                            <Typography
                                component="span"
                                onClick={() => navigate(`#meters`)}
                                sx={{
                                    color: vortxColors.gold,
                                    cursor: 'pointer',
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                Edit &rarr;
                            </Typography>
                        </Box>
                    </Box>

                    {/* Enabled Apps */}
                    {enabledApps.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: vortxColors.stone,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                mb: 1.5,
                            }}>
                                Enabled Apps ({enabledApps.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {enabledApps.map((appKey) => {
                                    const config = APP_CONFIG[appKey];
                                    const Icon = config.icon;
                                    const detail = getAppConfigDetail(appKey);

                                    return (
                                        <Paper
                                            key={appKey}
                                            onClick={() => handleAppClick(appKey)}
                                            sx={{
                                                p: 2,
                                                bgcolor: isRetro ? vortxColors.charcoal : 'background.paper',
                                                border: `1px solid ${isRetro ? vortxColors.teal : 'rgba(0,0,0,0.12)'}`,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: config.color,
                                                    boxShadow: `0 0 12px ${config.color}20`,
                                                },
                                            }}
                                        >
                                            <Icon sx={{ color: config.color, fontSize: 24, flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                                    <Typography sx={{
                                                        fontFamily: vortxTypography.heading,
                                                        fontWeight: 700,
                                                        color: isRetro ? vortxColors.white : 'text.primary',
                                                        fontSize: '0.9rem',
                                                    }}>
                                                        {config.label}
                                                    </Typography>
                                                    <Typography sx={{
                                                        color: vortxColors.stone,
                                                        fontSize: '0.7rem',
                                                        fontFamily: vortxTypography.mono,
                                                        opacity: 0.7,
                                                    }}>
                                                        {config.model}
                                                    </Typography>
                                                </Box>
                                                {detail && (
                                                    <Typography sx={{
                                                        color: vortxColors.stone,
                                                        fontSize: '0.75rem',
                                                        fontFamily: vortxTypography.mono,
                                                    }}>
                                                        {detail}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                                <Typography sx={{
                                                    color: config.color,
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.7rem',
                                                    textTransform: 'uppercase',
                                                    fontWeight: 600,
                                                }}>
                                                    Open
                                                </Typography>
                                                <LaunchIcon sx={{ color: config.color, fontSize: 14 }} />
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); setRemoveDialog({ open: true, appKey }); }}
                                                    sx={{
                                                        ml: 1,
                                                        color: vortxColors.stone,
                                                        '&:hover': { color: '#d32f2f', bgcolor: 'rgba(211, 47, 47, 0.1)' },
                                                    }}
                                                >
                                                    <DeleteOutline sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Available Apps */}
                    {disabledApps.length > 0 && (
                        <Box>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: vortxColors.stone,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                mb: 1.5,
                            }}>
                                Available
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {disabledApps.map((appKey) => {
                                    const config = APP_CONFIG[appKey];
                                    const Icon = config.icon;

                                    return (
                                        <Paper
                                            key={appKey}
                                            sx={{
                                                p: 2,
                                                bgcolor: isRetro ? 'rgba(44, 44, 44, 0.5)' : 'rgba(0, 0, 0, 0.04)',
                                                border: `1px solid ${isRetro ? 'rgba(63, 106, 100, 0.3)' : 'rgba(0,0,0,0.12)'}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                opacity: 0.7,
                                            }}
                                        >
                                            <Icon sx={{ color: vortxColors.stone, fontSize: 24, flexShrink: 0 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                                    <Typography sx={{
                                                        fontFamily: vortxTypography.heading,
                                                        fontWeight: 700,
                                                        color: vortxColors.stone,
                                                        fontSize: '0.9rem',
                                                    }}>
                                                        {config.label}
                                                    </Typography>
                                                    <Typography sx={{
                                                        color: vortxColors.stone,
                                                        fontSize: '0.7rem',
                                                        fontFamily: vortxTypography.mono,
                                                        opacity: 0.5,
                                                    }}>
                                                        {config.model}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Button
                                                size="small"
                                                onClick={() => handleEnableApp(appKey)}
                                                sx={{
                                                    color: vortxColors.gold,
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.7rem',
                                                    textTransform: 'uppercase',
                                                    fontWeight: 600,
                                                    border: `1px solid rgba(178, 140, 80, 0.3)`,
                                                    '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.1)', borderColor: vortxColors.gold },
                                                }}
                                            >
                                                Enable
                                            </Button>
                                        </Paper>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Load Profile Graphs */}
                    {project.meters?.main?.load_profile?.loads_kw?.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: vortxColors.stone,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                mb: 1.5,
                            }}>
                                Facility Load Profile
                            </Typography>
                            <Paper sx={{
                                p: 2,
                                bgcolor: isRetro ? vortxColors.charcoal : 'background.paper',
                                border: `1px solid ${isRetro ? vortxColors.teal : 'rgba(0,0,0,0.12)'}`,
                                borderRadius: 1,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography sx={{
                                        color: isRetro ? vortxColors.white : 'text.primary',
                                        fontSize: '0.8rem',
                                        fontFamily: vortxTypography.mono,
                                    }}>
                                        {Math.round(project.meters.main.load_profile.metrics?.peak_kw || Math.max(...project.meters.main.load_profile.loads_kw))} kW peak
                                    </Typography>
                                    <Typography sx={{
                                        color: vortxColors.stone,
                                        fontSize: '0.75rem',
                                        fontFamily: vortxTypography.mono,
                                    }}>
                                        {project.meters.main.load_profile.loads_kw.length.toLocaleString()} hrs
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <IconButton
                                        size="small"
                                        onClick={() => setTerrainDialogOpen(true)}
                                        sx={{ color: vortxColors.stone, '&:hover': { color: vortxColors.gold } }}
                                        title="Expand terrain view"
                                    >
                                        <FullscreenIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Box sx={{ height: { xs: 260, sm: 340, md: 420, lg: 480 }, cursor: 'pointer' }} onClick={() => setTerrainDialogOpen(true)}>
                                    <TerrainErrorBoundary fallback={<LoadProfileChart loads={project.meters.main.load_profile.loads_kw} color={vortxColors.gold} areaColor={vortxColors.gold} />}>
                                        <React.Suspense fallback={<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} sx={{ color: vortxColors.gold }} /></Box>}>
                                            <LoadProfileTerrain loads={project.meters.main.load_profile.loads_kw} isDark={isRetro} />
                                        </React.Suspense>
                                    </TerrainErrorBoundary>
                                </Box>
                            </Paper>

                            <Dialog
                                open={terrainDialogOpen}
                                onClose={() => setTerrainDialogOpen(false)}
                                maxWidth="lg"
                                fullWidth
                                PaperProps={{
                                    sx: {
                                        bgcolor: isRetro ? vortxColors.charcoal : 'background.paper',
                                        border: isRetro ? `1px solid ${vortxColors.teal}` : 'none',
                                    }
                                }}
                            >
                                <DialogTitle sx={{
                                    fontFamily: vortxTypography.mono,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    letterSpacing: '0.1em',
                                    color: isRetro ? vortxColors.white : 'text.primary',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}>
                                    Facility Load Profile — {project.meters.main.load_profile.loads_kw.length.toLocaleString()} hrs
                                    <IconButton
                                        size="small"
                                        onClick={() => setTerrainDialogOpen(false)}
                                        sx={{ color: vortxColors.stone, '&:hover': { color: vortxColors.gold } }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </DialogTitle>
                                <DialogContent sx={{ p: 0, height: '70vh' }}>
                                    <TerrainErrorBoundary fallback={<LoadProfileChart loads={project.meters.main.load_profile.loads_kw} color={vortxColors.gold} areaColor={vortxColors.gold} />}>
                                        <React.Suspense fallback={<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} sx={{ color: vortxColors.gold }} /></Box>}>
                                            <LoadProfileTerrain
                                                loads={project.meters.main.load_profile.loads_kw}
                                                isDark={isRetro}
                                                interactive
                                                touSchedule={project.meters?.main?.tariff}
                                                onDayClick={null}
                                            />
                                        </React.Suspense>
                                    </TerrainErrorBoundary>
                                </DialogContent>
                            </Dialog>
                        </Box>
                    )}

                    {project.meters?.ev_submeter?.load_profile?.loads_kw?.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.65rem',
                                color: vortxColors.stone,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                mb: 1.5,
                            }}>
                                EV Submeter Load Profile
                            </Typography>
                            <Paper sx={{
                                p: 2,
                                bgcolor: isRetro ? vortxColors.charcoal : 'background.paper',
                                border: `1px solid ${isRetro ? vortxColors.teal : 'rgba(0,0,0,0.12)'}`,
                                borderRadius: 1,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1 }}>
                                    <Typography sx={{
                                        color: isRetro ? vortxColors.white : 'text.primary',
                                        fontSize: '0.8rem',
                                        fontFamily: vortxTypography.mono,
                                    }}>
                                        {Math.round(project.meters.ev_submeter.load_profile.metrics?.peak_kw || Math.max(...project.meters.ev_submeter.load_profile.loads_kw))} kW peak
                                    </Typography>
                                    <Typography sx={{
                                        color: vortxColors.stone,
                                        fontSize: '0.75rem',
                                        fontFamily: vortxTypography.mono,
                                    }}>
                                        {project.meters.ev_submeter.load_profile.loads_kw.length.toLocaleString()} hrs
                                    </Typography>
                                </Box>
                                <Box sx={{ height: 180 }}>
                                    <LoadProfileChart
                                        loads={project.meters.ev_submeter.load_profile.loads_kw}
                                        color="#4CAF50"
                                        areaColor="#4CAF50"
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </>
            )}

            {/* ===== METERS & TARIFFS SECTION ===== */}
            {activeSection === 'meters' && (
                <>
                    {/* Location */}
                    <Paper sx={{ p: 3, mb: 4, bgcolor: isRetro ? vortxColors.charcoal : 'background.paper', border: `1px solid ${isRetro ? vortxColors.teal : 'rgba(0,0,0,0.12)'}` }}>
                        <Typography
                            variant="h6"
                            sx={{ fontFamily: vortxTypography.heading, color: isRetro ? vortxColors.white : 'text.primary', mb: 2, fontWeight: 700 }}
                        >
                            Location
                        </Typography>
                        <TextField
                            fullWidth
                            label="Address"
                            value={location.address}
                            onChange={(e) => setLocation(prev => ({ ...prev, address: e.target.value }))}
                            onBlur={handleLocationBlur}
                            size="small"
                            sx={{ ...fieldSx, mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="City"
                                value={location.city}
                                onChange={(e) => setLocation(prev => ({ ...prev, city: e.target.value }))}
                                onBlur={handleLocationBlur}
                                size="small"
                                sx={{ ...fieldSx, flex: 2 }}
                            />
                            <TextField
                                select
                                label="State"
                                value={location.state}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const updatedLoc = { ...location, state: val };
                                    setLocation(updatedLoc);
                                    handleLocationSave(updatedLoc);
                                }}
                                size="small"
                                sx={{ ...fieldSx, flex: 1.5, minWidth: 160 }}
                                SelectProps={{
                                    MenuProps: {
                                        PaperProps: {
                                            sx: {
                                                bgcolor: isRetro ? vortxColors.charcoal : '#fff',
                                                border: isRetro ? `1px solid ${vortxColors.teal}` : 'none',
                                                maxHeight: 300,
                                                '& .MuiMenuItem-root': {
                                                    color: isRetro ? vortxColors.white : 'text.primary',
                                                    fontSize: '0.875rem',
                                                    '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.15)' },
                                                    '&.Mui-selected': { bgcolor: 'rgba(178, 140, 80, 0.25)' },
                                                    '&.Mui-selected:hover': { bgcolor: 'rgba(178, 140, 80, 0.3)' },
                                                },
                                            },
                                        },
                                    },
                                }}
                            >
                                {US_STATES.map((s) => (
                                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="ZIP"
                                value={location.zip_code}
                                onChange={(e) => setLocation(prev => ({ ...prev, zip_code: e.target.value }))}
                                onBlur={handleLocationBlur}
                                size="small"
                                sx={{ ...fieldSx, flex: 1, minWidth: 100 }}
                            />
                        </Box>
                    </Paper>

                    {/* Meters & Tariffs */}
                    <MetersAndTariffsSection
                        siteAmps={siteAmps}
                        serviceVoltage={serviceVoltage}
                        onSiteAmpsChange={setSiteAmps}
                        onServiceVoltageChange={(v) => {
                            setServiceVoltage(v);
                            // Save immediately on dropdown change — include amps so they persist too
                            const amps = parseFloat(siteAmps);
                            const volts = parseInt(v);
                            const kw = (amps > 0 && volts > 0) ? Math.round(amps * volts * Math.sqrt(3) / 100) / 10 : null;
                            saveField({
                                site_capacity_amps: amps > 0 ? amps : null,
                                service_voltage: volts > 0 ? volts : null,
                                site_capacity_kw: kw,
                            });
                        }}
                        onSiteElectricalSave={() => {
                            const amps = parseFloat(siteAmps);
                            const volts = parseInt(serviceVoltage);
                            const kw = (amps > 0 && volts > 0) ? Math.round(amps * volts * Math.sqrt(3) / 100) / 10 : null;
                            saveField({
                                site_capacity_amps: amps > 0 ? amps : null,
                                service_voltage: volts > 0 ? volts : null,
                                site_capacity_kw: kw,
                            });
                        }}
                        project={project}
                        onUpdateMeters={handleUpdateMeters}
                        onUpdateAppConfig={handleUpdateAppConfig}
                        onDecompose={
                            project?.apps?.flux_cold?.enabled
                                ? handleDecompose
                                : undefined
                        }
                    />
                </>
            )}

            {/* Remove App Confirmation Dialog */}
            <Dialog
                open={removeDialog.open}
                onClose={() => setRemoveDialog({ open: false, appKey: null })}
                PaperProps={{
                    sx: {
                        bgcolor: isRetro ? vortxColors.charcoal : 'background.paper',
                        border: `1px solid ${isRetro ? vortxColors.teal : 'rgba(0,0,0,0.12)'}`,
                        color: isRetro ? vortxColors.white : 'text.primary',
                    },
                }}
            >
                <DialogTitle sx={{ fontFamily: vortxTypography.heading, color: isRetro ? vortxColors.white : 'text.primary' }}>
                    Remove {removeDialog.appKey && APP_CONFIG[removeDialog.appKey]?.label}?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: vortxColors.stone }}>
                        This will delete {removeDialog.appKey && getAppDataDescription(removeDialog.appKey)} for {removeDialog.appKey && APP_CONFIG[removeDialog.appKey]?.label} on this project. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setRemoveDialog({ open: false, appKey: null })}
                        sx={{ color: vortxColors.stone }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRemoveApp}
                        sx={{
                            color: '#d32f2f',
                            fontWeight: 600,
                            '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' },
                        }}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
